import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { RankingEntry } from '../../types/analytics';

interface ComparisonBarChartProps {
  data: RankingEntry[];
  title?: string;
  height?: number;
  showRank?: boolean;
  horizontal?: boolean;
  colorByPerformance?: boolean;
  limit?: number;
  animate?: boolean;
}

const COLORS = {
  excellent: '#10b981', // green
  good: '#22c55e',
  average: '#f59e0b', // amber
  poor: '#ef4444', // red
  critical: '#dc2626',
};

const getPerformanceColor = (percent: number): string => {
  if (percent >= 95) return COLORS.excellent;
  if (percent >= 85) return COLORS.good;
  if (percent >= 70) return COLORS.average;
  if (percent >= 50) return COLORS.poor;
  return COLORS.critical;
};

const ComparisonBarChart: React.FC<ComparisonBarChartProps> = ({
  data,
  title = 'Attendance Comparison',
  height = 400,
  showRank = true,
  horizontal = true,
  colorByPerformance = true,
  limit = 10,
  animate = true,
}) => {
  const chartData = useMemo(() => {
    return data.slice(0, limit).map(entry => ({
      ...entry,
      displayName: showRank ? `#${entry.rank} ${entry.name}` : entry.name,
      color: colorByPerformance ? getPerformanceColor(entry.attendancePercent) : '#6B1786',
    }));
  }, [data, limit, showRank, colorByPerformance]);

  const option = useMemo(() => {
    if (!chartData.length) return {};

    const names = chartData.map(d => d.displayName);
    const values = chartData.map(d => d.attendancePercent);
    const colors = chartData.map(d => d.color);

    const baseConfig = {
      title: title ? {
        text: title,
        left: 'center',
        top: 10,
        textStyle: { fontSize: 16, fontWeight: 600, color: '#374151' }
      } : undefined,
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        textStyle: { color: '#374151' },
        formatter: (params: any[]) => {
          const param = params[0];
          const entry = chartData[param.dataIndex];
          
          // Safety checks for undefined values
          if (!entry) return '';
          
          const attendancePercent = entry.attendancePercent ?? 0;
          const trendPercent = entry.trendPercent ?? 0;
          const trendDirection = entry.trendDirection ?? 'stable';
          const rankChange = entry.rankChange ?? 0;
          
          const trendIcon = trendDirection === 'up' ? '↑' : trendDirection === 'down' ? '↓' : '→';
          const trendColor = trendDirection === 'up' ? '#10b981' : trendDirection === 'down' ? '#ef4444' : '#6b7280';
          
          return `
            <div style="min-width: 180px;">
              <div style="font-weight: 600; margin-bottom: 8px;">${entry.name}</div>
              ${entry.parentName ? `<div style="color: #6b7280; font-size: 12px; margin-bottom: 8px;">${entry.parentName}</div>` : ''}
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span>Attendance:</span>
                <span style="font-weight: 600;">${attendancePercent.toFixed(1)}%</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span>Trend:</span>
                <span style="color: ${trendColor}; font-weight: 600;">
                  ${trendIcon} ${Math.abs(trendPercent).toFixed(1)}%
                </span>
              </div>
              ${rankChange !== 0 ? `
                <div style="display: flex; justify-content: space-between;">
                  <span>Rank Change:</span>
                  <span style="color: ${rankChange > 0 ? '#10b981' : '#ef4444'}; font-weight: 600;">
                    ${rankChange > 0 ? '+' : ''}${rankChange}
                  </span>
                </div>
              ` : ''}
            </div>
          `;
        }
      },
      grid: {
        left: horizontal ? 150 : 60,
        right: 30,
        top: title ? 60 : 30,
        bottom: horizontal ? 30 : 80,
      },
    };

    if (horizontal) {
      return {
        ...baseConfig,
        xAxis: {
          type: 'value',
          min: 0,
          max: 100,
          axisLabel: {
            formatter: '{value}%',
            fontSize: 10,
            color: '#6b7280'
          },
          splitLine: { lineStyle: { color: '#f3f4f6' } },
        },
        yAxis: {
          type: 'category',
          data: names.reverse(),
          axisLabel: {
            fontSize: 11,
            color: '#374151',
            width: 140,
            overflow: 'truncate',
          },
          axisLine: { show: false },
          axisTick: { show: false },
        },
        series: [{
          type: 'bar',
          data: values.reverse().map((v, i) => ({
            value: v,
            itemStyle: { 
              color: colors.reverse()[i],
              borderRadius: [0, 4, 4, 0],
            }
          })),
          barWidth: '60%',
          label: {
            show: true,
            position: 'right',
            formatter: '{c}%',
            fontSize: 11,
            color: '#374151',
            fontWeight: 500,
          },
          animationDuration: animate ? 1000 : 0,
          animationEasing: 'elasticOut',
        }],
      };
    }

    return {
      ...baseConfig,
      xAxis: {
        type: 'category',
        data: names,
        axisLabel: {
          rotate: 45,
          fontSize: 10,
          color: '#374151',
          interval: 0,
        },
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        axisLabel: {
          formatter: '{value}%',
          fontSize: 10,
          color: '#6b7280'
        },
        splitLine: { lineStyle: { color: '#f3f4f6' } },
      },
      series: [{
        type: 'bar',
        data: values.map((v, i) => ({
          value: v,
          itemStyle: { 
            color: colors[i],
            borderRadius: [4, 4, 0, 0],
          }
        })),
        barWidth: '60%',
        label: {
          show: true,
          position: 'top',
          formatter: '{c}%',
          fontSize: 10,
          color: '#374151',
          fontWeight: 500,
        },
        animationDuration: animate ? 1000 : 0,
        animationEasing: 'elasticOut',
      }],
    };
  }, [chartData, title, horizontal, animate]);

  if (!data || data.length === 0) {
    return (
      <div 
        className="flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200"
        style={{ height }}
      >
        <p className="text-gray-500">No comparison data available</p>
      </div>
    );
  }

  return (
    <ReactECharts 
      option={option} 
      style={{ height, width: '100%' }}
      opts={{ renderer: 'svg' }}
    />
  );
};

export default ComparisonBarChart;
